// src/client/lib/api.ts
//
// Phase B+ T1: API fetch wrapper (Authorization 自動付与 + patchJson / deleteJson 追加).
//
// 設計方針 (MVP):
//  - 2 origin 構成 (Pages = client / Workers = API) を前提
//  - API base URL は VITE_API_BASE_URL (env)、未設定時は localhost:8787 fallback
//  - response 型は server route の `{ ok: true, data } | { ok: false, error, message }` 形式
//  - **Authorization は sessionStorage から自動付与** (token が存在する場合のみ)
//  - retry / cache 機能は未実装 (TanStack Query 側で吸収)
//  - cross-origin → CORS は API 側 (Phase B T7 で wrangler 設定済)
//
// 参照:
//  - docs/plans/phase-b-frontend-mvp-plan.md Q1 (2 origin) / Q2 (sessionStorage)
//  - docs/plans/phase-b-plus-write-ops-plan.md §2-1 T1
//  - src/server/routes/auth.ts (response shape)
//
// #701: **session 自体が無効になった 401** (別の端末でのパスワード変更・ログアウト / 無効化された user /
//   期限切れ) を **ここ 1 箇所** で検知する (notifyIfSessionInvalid)。 保存した session を消し、
//   SESSION_INVALID_EVENT を投げる → AuthProvider が未 login 状態に戻し、 ProtectedRoute が
//   理由つきで login へ送る。 公開ページ (再設定 / 招待 / LIFF) は auth を要求しないので遷移しない。
//   対象は **session token を付けて送った request の 401 で、 error が下の 3 つのときだけ**。
//   login の PW 違い (token 無し) / PW 変更の現在 PW 違い (INVALID_CREDENTIALS) などは従来どおり呼び出し元が扱う。

import i18next from "i18next";
import { clearAuth, loadAuth } from "./session-storage";

const RAW_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").trim();
const API_BASE: string = RAW_BASE !== "" ? RAW_BASE.replace(/\/$/, "") : "http://localhost:8787";

export type ApiSuccess<T> = {
  readonly ok: true;
  readonly data: T;
};

export type ApiFailure = {
  readonly ok: false;
  readonly error: string;
  readonly message: string;
};

export type ApiResponse<T> = ApiSuccess<T> | ApiFailure;

/**
 * ネットワーク / parse 失敗を表す。
 * - response 受信前 (DNS / TCP / CORS preflight 失敗 等) は `status === undefined`
 * - response 受信後の JSON parse 失敗 / shape mismatch は `status` に HTTP status を保持
 *   (consumer が 401/403 を判定して clearAuth する等の defensive 経路で参照)
 */
export class ApiNetworkError extends Error {
  readonly status?: number;
  readonly cause?: unknown;
  constructor(message: string, cause?: unknown, status?: number) {
    super(message);
    this.name = "ApiNetworkError";
    this.cause = cause;
    this.status = status;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function assertPath(path: string, helperName: string): void {
  if (!path.startsWith("/")) {
    throw new Error(`${helperName} path must start with /, got: ${path}`);
  }
}

/**
 * sessionStorage に保存された JWT を Authorization Bearer ヘッダに変換。
 * token が存在しない場合は空オブジェクト = ヘッダ付与なし。
 * (login pre-auth では token なしで OK、post-auth では自動付与)
 */
function buildAuthHeader(): Record<string, string> {
  const t = loadAuth()?.token;
  if (!t) return {};
  return { Authorization: `Bearer ${t}` };
}

/** #701: session が無効になったことを AuthProvider に伝える window event の名前 */
export const SESSION_INVALID_EVENT = "housing-os:session-invalid";

/** #701: login 画面に出す理由 (auth.notice.<値>) */
export type SessionNotice = "sessionRevoked" | "sessionInactive" | "sessionExpired";

/**
 * #701: session 自体が無効になったことを示す 401 の error code → 理由。 **この 3 つだけ**。
 *   AUTH_TOKEN_REVOKED = logout / パスワード変更 / 再設定で失効 (middleware/auth.ts)
 *   USER_INACTIVE      = user が無効化 / 削除された
 *   AUTH_TOKEN_INVALID = 署名不正 / 期限切れ ("Invalid or expired token")
 */
const SESSION_INVALID_CODES: Readonly<Record<string, SessionNotice>> = {
  AUTH_TOKEN_REVOKED: "sessionRevoked",
  USER_INACTIVE: "sessionInactive",
  AUTH_TOKEN_INVALID: "sessionExpired",
};

/** 送った request の session token (無ければ null) */
function sentSessionToken(): string | null {
  return loadAuth()?.token ?? null;
}

/**
 * #701: session 無効の 401 を検知する **唯一の箇所**。
 *   - token を付けて送った request だけ (付けていない login 等は対象外 → login 画面で loop しない)
 *   - 保存中の token が **送った token と同じ** ときだけ消す (その間に別 tab で login し直した session を消さない)
 *   - 消した後は token が無いので、 以降の request で再び発火しない
 */
function notifyIfSessionInvalid(status: number, body: unknown, sentToken: string | null): void {
  if (sentToken === null || status !== 401) return;
  const code = typeof body === "object" && body !== null ? (body as { error?: unknown }).error : undefined;
  if (typeof code !== "string" || !Object.prototype.hasOwnProperty.call(SESSION_INVALID_CODES, code)) return;
  if (loadAuth()?.token !== sentToken) return;
  clearAuth();
  const notice = SESSION_INVALID_CODES[code];
  try {
    window.dispatchEvent(new CustomEvent(SESSION_INVALID_EVENT, { detail: { notice } }));
  } catch {
    // window の無い環境 (test 等) — session は消してある
  }
}

/**
 * gap-k3 #757: JSON でない応答の利用者向けの説明 (status と content-type だけ。 body は入れない)。
 * 非 JSON の 404 は 「この API がまだ配備されていない (Worker の配備待ち・失敗) / 接続先が違う」 ことが多い。
 * 文言は i18n (api.error.notJson*)。 i18n の初期化前 (test 等) は英語の既定文。
 */
export function nonJsonMessage(status: number, contentType: string): string {
  const key = status === 404 ? "api.error.notJson404" : "api.error.notJson";
  const msg = i18next.t(key, { status, contentType });
  if (typeof msg === "string" && msg !== key) return msg;
  return status === 404
    ? `The API for this screen is not available (HTTP 404, non-JSON ${contentType}). It may not be deployed yet. Please reload later.`
    : `The server returned a non-JSON response (HTTP ${status}, ${contentType}). Please reload.`;
}

async function parseJsonOrThrow<T>(res: Response, sentToken: string | null = null): Promise<ApiResponse<T>> {
  // Phase 14 Step 3 (JSON error 根治): JSON parse 失敗時に診断情報 (status / content-type /
  // body snippet) を error message に含める。 user / developer が「何が返ってきたか」 を
  // 即座に判別できる (= auth login HTML redirect / Cloudflare Pages 404 / CORS 失敗 等)。
  // 旧: "Response is not valid JSON" のみで原因不明だった。
  let bodyText: string;
  try {
    bodyText = await res.text();
  } catch (err) {
    throw new ApiNetworkError(
      `Failed to read response body (status=${res.status})`,
      err,
      res.status,
    );
  }
  let json: unknown;
  try {
    json = JSON.parse(bodyText);
  } catch (err) {
    const contentType = res.headers.get("content-type") ?? "(none)";
    const snippet = bodyText.length > 200 ? `${bodyText.slice(0, 200)}…` : bodyText;
    // gap-k3 #757: 生の body は画面に出さない (業務画面・帳票に 「404 Not Found」 等の内部の文字列を出さない)。
    //   開発側の診断 (status / content-type / URL / body の先頭) は console.error に残す。
    //   画面には 「API に接続できない (配備・接続先の問題の可能性)」 という説明だけを出す (失敗を 0 件・成功に見せない)
    console.error("[api] JSON でない応答", { status: res.status, contentType, url: res.url, body: snippet });
    throw new ApiNetworkError(nonJsonMessage(res.status, contentType), err, res.status);
  }
  notifyIfSessionInvalid(res.status, json, sentToken);
  if (typeof json === "object" && json !== null && "ok" in json) {
    return json as ApiResponse<T>;
  }
  throw new ApiNetworkError(
    `Response shape is not ApiResponse (status=${res.status}, body=${bodyText.slice(0, 200)})`,
    undefined,
    res.status,
  );
}

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export type FetchOptions = {
  /** AbortSignal */
  readonly signal?: AbortSignal;
};

// ---------------------------------------------------------------------------
// HTTP method helpers
// ---------------------------------------------------------------------------

export async function postJson<TResponse>(
  path: string,
  body: unknown,
  options: FetchOptions = {},
): Promise<ApiResponse<TResponse>> {
  assertPath(path, "postJson");
  const sentToken = sentSessionToken();
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...buildAuthHeader(),
      },
      body: JSON.stringify(body),
      signal: options.signal,
    });
  } catch (err) {
    throw new ApiNetworkError("Network request failed", err);
  }
  return parseJsonOrThrow<TResponse>(res, sentToken);
}

export async function getJson<TResponse>(
  path: string,
  options: FetchOptions = {},
): Promise<ApiResponse<TResponse>> {
  assertPath(path, "getJson");
  const sentToken = sentSessionToken();
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        ...buildAuthHeader(),
      },
      signal: options.signal,
    });
  } catch (err) {
    throw new ApiNetworkError("Network request failed", err);
  }
  return parseJsonOrThrow<TResponse>(res, sentToken);
}

export async function patchJson<TResponse>(
  path: string,
  body: unknown,
  options: FetchOptions = {},
): Promise<ApiResponse<TResponse>> {
  assertPath(path, "patchJson");
  const sentToken = sentSessionToken();
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...buildAuthHeader(),
      },
      body: JSON.stringify(body),
      signal: options.signal,
    });
  } catch (err) {
    throw new ApiNetworkError("Network request failed", err);
  }
  return parseJsonOrThrow<TResponse>(res, sentToken);
}

export async function deleteJson<TResponse>(
  path: string,
  options: FetchOptions = {},
): Promise<ApiResponse<TResponse>> {
  assertPath(path, "deleteJson");
  const sentToken = sentSessionToken();
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        ...buildAuthHeader(),
      },
      signal: options.signal,
    });
  } catch (err) {
    throw new ApiNetworkError("Network request failed", err);
  }
  return parseJsonOrThrow<TResponse>(res, sentToken);
}

/**
 * #702 (gap-a2): body つきの PUT / DELETE (管理の設定画面: 機能フラグの PUT、 役割の削除の expectedUpdatedAt)。
 * 既存の helper は変えずに足す (認証ヘッダ・応答の扱いは patchJson と同じ)。
 */
async function sendJsonWithBody<TResponse>(
  method: "PUT" | "DELETE",
  path: string,
  body: unknown,
  options: FetchOptions,
): Promise<ApiResponse<TResponse>> {
  assertPath(path, method === "PUT" ? "putJson" : "deleteJsonWithBody");
  const sentToken = sentSessionToken();
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...buildAuthHeader(),
      },
      body: JSON.stringify(body),
      signal: options.signal,
    });
  } catch (err) {
    throw new ApiNetworkError("Network request failed", err);
  }
  return parseJsonOrThrow<TResponse>(res, sentToken);
}

/** PUT。 #685 振込口座の登録・変更 (`PUT /admin/vendor-bank-accounts/:companyId`)・ #694 営業見込の登録・更新・ #702 機能フラグで使う。 */
export async function putJson<TResponse>(
  path: string,
  body: unknown,
  options: FetchOptions = {},
): Promise<ApiResponse<TResponse>> {
  return sendJsonWithBody<TResponse>("PUT", path, body, options);
}

export async function deleteJsonWithBody<TResponse>(
  path: string,
  body: unknown,
  options: FetchOptions = {},
): Promise<ApiResponse<TResponse>> {
  return sendJsonWithBody<TResponse>("DELETE", path, body, options);
}

/** dev / staging / production などの API base を露出 (debug / 表示用)。 */
export function getApiBase(): string {
  return API_BASE;
}

// ---------------------------------------------------------------------------
// Phase B' T6: multipart/form-data upload + blob download
// ---------------------------------------------------------------------------

/**
 * FormData (multipart) で POST upload を行う.
 *  - Content-Type は browser に任せる (boundary 自動付与のため明示しない)
 *  - Authorization は sessionStorage の JWT を自動付与
 *  - response shape は通常の `ApiResponse<T>` を期待
 */
export async function uploadFormData<TResponse>(
  path: string,
  formData: FormData,
  options: FetchOptions = {},
): Promise<ApiResponse<TResponse>> {
  assertPath(path, "uploadFormData");
  const sentToken = sentSessionToken();
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        ...buildAuthHeader(),
        // Content-Type を **明示しない** ことで browser が multipart boundary を自動付与
      },
      body: formData,
      signal: options.signal,
    });
  } catch (err) {
    throw new ApiNetworkError("Network request failed", err);
  }
  return parseJsonOrThrow<TResponse>(res, sentToken);
}

/**
 * gap-b2b (#685 F07): JSON を POST して **ファイル (blob)** を受け取る (振込データの作成など)。
 *  - 成功 (2xx) は blob を返す。 呼び出し側が保存する (画面に中身を出さない)
 *  - 失敗は server の `{ ok: false, error, message }` を読み、 ApiFailure として返す (blob は返さない)
 *  - session 無効の 401 は getJson と同じく notifyIfSessionInvalid で扱う
 */
export async function postJsonForBlob(
  path: string,
  body: unknown,
  options: FetchOptions = {},
): Promise<{ readonly ok: true; readonly blob: Blob; readonly contentType: string } | ApiFailure> {
  assertPath(path, "postJsonForBlob");
  const sentToken = sentSessionToken();
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...buildAuthHeader(),
      },
      body: JSON.stringify(body),
      signal: options.signal,
    });
  } catch (err) {
    throw new ApiNetworkError("Network request failed", err);
  }
  if (!res.ok) {
    // 失敗は JSON (server の共通形)。 JSON でなければ parseJsonOrThrow が診断つきで throw する
    const failure = await parseJsonOrThrow<never>(res, sentToken);
    if (failure.ok) throw new ApiNetworkError(`HTTP ${res.status}`, undefined, res.status);
    return failure;
  }
  const blob = await res.blob();
  return { ok: true, blob, contentType: res.headers.get("Content-Type") ?? "application/octet-stream" };
}

/**
 * 認証付き GET で blob を取得 (画像 / PDF preview や download に使用).
 *  - response が non-2xx の場合は ApiNetworkError(status) を throw
 *  - 成功時は blob を返却 (caller が URL.createObjectURL で blob URL 化)
 */
export async function fetchAuthedBlob(
  path: string,
  options: FetchOptions = {},
): Promise<{ blob: Blob; contentType: string }> {
  assertPath(path, "fetchAuthedBlob");
  const sentToken = sentSessionToken();
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "GET",
      headers: {
        ...buildAuthHeader(),
      },
      signal: options.signal,
    });
  } catch (err) {
    throw new ApiNetworkError("Network request failed", err);
  }
  if (!res.ok) {
    if (res.status === 401) {
      // #701: blob の取得でも session 無効の 401 は同じ扱い (body は JSON のときだけ読む)
      const body: unknown = await res.json().catch(() => null);
      notifyIfSessionInvalid(res.status, body, sentToken);
    }
    throw new ApiNetworkError(`HTTP ${res.status}`, undefined, res.status);
  }
  const blob = await res.blob();
  const contentType = res.headers.get("Content-Type") ?? "application/octet-stream";
  return { blob, contentType };
}
